import React, { useState, useMemo } from 'react';
import { Image, Typography } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../lib/constans';

const { Title } = Typography;

export default function GuestAccommodation({ decks }) {
  if (!decks || !decks.length) {
    return <div className="text-center p-8 text-white">Cargando datos de acomodación...</div>;
  }

  const galleryImages = useMemo(() => {
    const deckItems = decks.filter(deck => deck.type !== 'Each Cabin' && deck.title !== 'Each Cabin Has:');

    let globalIndex = 0;
    const items = [];

    deckItems.forEach((deck) => {
      if (deck.images && Array.isArray(deck.images)) {
        deck.images.forEach((imgUrl) => {
          const fullSrc = imgUrl.startsWith('http') ? imgUrl : `${API_BASE_URL}${imgUrl}`;
          items.push({
            id: globalIndex++,
            src: fullSrc,
            label: `${deck.title}\n${Array.isArray(deck.coments) ? deck.coments.join('\n') : deck.coments || ''}`
          });
        });
      }
    });

    return items;
  }, [decks]);

  const mainDeckItems = useMemo(() => decks.filter(deck => deck.type === 'Main Deck'), [decks]);
  const upperDeckItems = useMemo(() => decks.filter(deck => deck.type === 'Upper Deck'), [decks]);
  const lowerDeckItems = useMemo(() => decks.filter(deck => deck.type === 'Lower Deck'), [decks]);
  const amenitiesItem = useMemo(() => decks.find(deck => deck.type === 'Each Cabin' || deck.title === 'Each Cabin Has:'), [decks]);

  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => {
    if (galleryImages.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    if (galleryImages.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <section className="w-full py-12 font-sans">

      <div className="text-center mb-8">
        <span className="inline-block md:px-8 py-2 text-2xl md:text-3xl font-bold text-p-blue tracking-wider uppercase border-b-4 border-p-orange">
          GUEST ACCOMMODATION
        </span>
      </div>

      {galleryImages.length > 0 && (
        <>
          <div className="relative w-full aspect-[9/9] md:aspect-[21/8] bg-gray-900/10 overflow-hidden shadow-md group">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 w-full h-full"
              >
                <Image
                  src={galleryImages[activeIndex]?.src}
                  alt="Guest Accommodation Large View"
                  fill
                  sizes="(max-width: 1024px) 100vw, 1000px"
                  className="object-cover"
                  priority
                  unoptimized
                />
              </motion.div>
            </AnimatePresence>

            <button aria-label="Prev slide" onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white p-3 rounded-full transition-all z-10">
              <LeftOutlined className="text-base flex" />
            </button>

            <button aria-label="Next slide" onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white p-3 rounded-full transition-all z-10">
              <RightOutlined className="text-base flex" />
            </button>
          </div>
          <div className="max-w-7xl mx-auto w-full mt-10 overflow-x-auto flex gap-3 scrollbar-none !px-3">
            {galleryImages.map((img, index) => (
              <div
                key={img.id}
                onClick={() => setActiveIndex(index)}
                className={`relative h-30 w-[180px] flex-shrink-0 rounded-md overflow-hidden cursor-pointer transition-all duration-200 snap-start ${activeIndex === index
                  ? 'ring-2 ring-p-orange scale-95 shadow-md'
                  : 'opacity-65 hover:opacity-100 border border-gray-200'
                  }`}
              >
                <img
                  src={img.src}
                  alt={`Cabin Thumbnail ${index + 1}`}
                  fill
                  sizes="140px"
                  className="object-cover"
                  unoptimized
                />
              </div>
            ))}
          </div>
        </>
      )}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 pt-8 border-t border-gray-100 p-4">
        <div className="flex flex-col gap-8">

          {lowerDeckItems.length > 0 && (
            <div>
              <Title level={3} className="!text-p-orange !font-bold !text-2xl !mb-4 border-b-4 border-p-blue">Lower Deck</Title>
              <div className="flex flex-col gap-4">
                {lowerDeckItems.map((item, idx) => (
                  <div key={idx} className="text-p-blue">
                    <span className="font-bold block text-base">{item.title}</span>
                    <span className="text-sm leading-relaxed block mt-0.5">
                      {Array.isArray(item.coments) ? item.coments.join(' ') : item.coments}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mainDeckItems.length > 0 && (
            <div>
              <Title level={3} className="!text-p-orange !font-bold !text-2xl !mb-4 border-b-4 border-p-blue">Main Deck</Title>
              <div className="flex flex-col gap-4">
                {mainDeckItems.map((item, idx) => (
                  <div key={idx} className="text-p-blue">
                    <span className="font-bold block text-base">{item.title}</span>
                    <span className="text-sm leading-relaxed block mt-0.5">
                      {Array.isArray(item.coments) ? item.coments.join(' ') : item.coments}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {upperDeckItems.length > 0 && (
            <div>
              <Title level={3} className="!text-p-orange !font-bold !text-2xl !mb-4 border-b-4 border-p-blue">Upper Deck</Title>
              <div className="flex flex-col gap-4">
                {upperDeckItems.map((item, idx) => (
                  <div key={idx} className="text-p-blue">
                    <span className="font-bold block text-base">{item.title}</span>
                    <span className="text-sm leading-relaxed block mt-0.5">
                      {Array.isArray(item.coments) ? item.coments.join(' ') : item.coments}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {amenitiesItem && (
          <div className="md:px-6 rounded-xl h-fit">
            <Title level={3} className="!text-p-orange !font-bold !text-2xl !mb-4 border-b-4 border-p-blue">
              {amenitiesItem.title || "Each Cabin Has:"}
            </Title>
            <ul className="space-y-3 m-0 p-0 list-none">
              {Array.isArray(amenitiesItem.coments) ? (
                amenitiesItem.coments.map((amenity, idx) => (
                  <li key={idx} className="flex items-start text-p-blue font-medium text-[14px] md:text-[15px]">
                    <span className="text-p-orange mr-2.5 text-xs mt-0.5">⚙</span>
                    {amenity}
                  </li>
                ))
              ) : (
                <li className="flex items-start text-p-blue font-medium text-[15px]">
                  <span className="text-p-orange mr-2.5 text-xs mt-0.5">⚙</span>
                  {amenitiesItem.coments}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

    </section>
  );
}
